import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, Upload, Image as ImageIcon, Tag, Trash2, Save, 
  Play, Download, Loader2, CheckCircle2, X, FileArchive, Cloud, Wand2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const DISRUPTIVE_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/qx3y7mcn_Recurso%202lgo%20disr.png";
const CASM_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dmndty59_San_Martin_deTucuman.png";

export default function TrainingPage() {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(null);
  const [models, setModels] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isAutoLabeling, setIsAutoLabeling] = useState(false);
  
  const [roboflowApiKey, setRoboflowApiKey] = useState('');
  const [roboflowWorkspace, setRoboflowWorkspace] = useState('');
  const [roboflowProject, setRoboflowProject] = useState('');
  const [roboflowVersion, setRoboflowVersion] = useState('1');
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentBox, setCurrentBox] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const fileInputRef = useRef(null);
  const zipInputRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [imagesRes, modelsRes] = await Promise.all([
        axios.get(`${API}/training/images`, { withCredentials: true }),
        axios.get(`${API}/training/models`, { withCredentials: true })
      ]);
      setImages(imagesRes.data);
      setModels(modelsRes.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setIsUploading(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    try {
      await axios.post(`${API}/training/images`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${files.length} imagen(es) subida(s)`);
      fetchData();
    } catch (err) {
      toast.error('Error al subir imagenes');
    } finally {
      setIsUploading(false);
    }
  };

  const loadImage = (image) => {
    setSelectedImage(image);
    setAnnotations(image.annotations || []);
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    drawAnnotations();
  };

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const handleMouseDown = (e) => {
    const pos = getMousePos(e);
    setIsDrawing(true);
    setStartPoint(pos);
    setCurrentBox({ x: pos.x, y: pos.y, width: 0, height: 0 });
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !startPoint) return;
    const pos = getMousePos(e);
    setCurrentBox({
      x: Math.min(startPoint.x, pos.x), y: Math.min(startPoint.y, pos.y),
      width: Math.abs(pos.x - startPoint.x), height: Math.abs(pos.y - startPoint.y)
    });
    drawAnnotations();
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentBox || currentBox.width < 10 || currentBox.height < 10) {
      setIsDrawing(false); setStartPoint(null); setCurrentBox(null);
      return;
    }
    const canvas = canvasRef.current;
    const newAnnotation = {
      class: 'patente',
      x_center: (currentBox.x + currentBox.width / 2) / canvas.width,
      y_center: (currentBox.y + currentBox.height / 2) / canvas.height,
      width: currentBox.width / canvas.width,
      height: currentBox.height / canvas.height
    };
    setAnnotations([...annotations, newAnnotation]);
    setIsDrawing(false); setStartPoint(null); setCurrentBox(null);
  };

  const drawAnnotations = () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !imageLoaded) return;
    const ctx = canvas.getContext('2d');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.drawImage(img, 0, 0);

    annotations.forEach((ann) => {
      const x = (ann.x_center - ann.width / 2) * canvas.width;
      const y = (ann.y_center - ann.height / 2) * canvas.height;
      const w = ann.width * canvas.width;
      const h = ann.height * canvas.height;
      ctx.strokeStyle = '#22c55e'; ctx.lineWidth = 3;
      ctx.strokeRect(x, y, w, h);
      ctx.fillStyle = '#22c55e';
      ctx.fillRect(x, y - 24, 80, 24);
      ctx.fillStyle = '#fff'; ctx.font = 'bold 14px sans-serif';
      ctx.fillText('PATENTE', x + 8, y - 7);
    });

    if (currentBox) {
      ctx.strokeStyle = '#3b82f6'; ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(currentBox.x, currentBox.y, currentBox.width, currentBox.height);
      ctx.setLineDash([]);
    }
  };

  useEffect(() => {
    if (imageLoaded) drawAnnotations();
    // drawAnnotations uses the state values listed above and is intentionally
    // recreated during render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations, currentBox, imageLoaded]);

  const removeAnnotation = (index) => {
    setAnnotations(annotations.filter((_, i) => i !== index));
  };

  const saveAnnotations = async () => {
    if (!selectedImage) return;
    try {
      await axios.post(`${API}/training/images/${selectedImage.id}/annotations`, { annotations }, { withCredentials: true });
      fetchData();
      toast.success('Anotaciones guardadas');
    } catch (err) {
      toast.error('Error al guardar');
    }
  };

  const autoLabelImage = async () => {
    if (!selectedImage) return;
    setIsAutoLabeling(true);
    try {
      const { data } = await axios.post(`${API}/training/images/${selectedImage.id}/auto-label`, {}, { withCredentials: true });
      toast.success(data.message);
      // Refresh image data
      const { data: imgData } = await axios.get(`${API}/training/images`, { withCredentials: true });
      setImages(imgData);
      const updatedImg = imgData.find(i => i.id === selectedImage.id);
      if (updatedImg) {
        setAnnotations(updatedImg.annotations || []);
        setSelectedImage(updatedImg);
      }
    } catch (err) {
      toast.error('Error en auto-etiquetado');
    } finally {
      setIsAutoLabeling(false);
    }
  };

  const deleteImage = async (imageId) => {
    try {
      await axios.delete(`${API}/training/images/${imageId}`, { withCredentials: true });
      if (selectedImage?.id === imageId) { setSelectedImage(null); setAnnotations([]); }
      toast.success('Imagen eliminada');
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const startTraining = async () => {
    if (annotatedCount < 5) {
      toast.error('Necesitas al menos 5 imagenes anotadas');
      return;
    }
    setIsTraining(true);
    setTrainingProgress({ status: 'starting', progress: 0 });
    try {
      const { data } = await axios.post(`${API}/training/start`, { epochs: 30 }, { withCredentials: true });
      toast.success(data.message);
      const pollProgress = async () => {
        try {
          const { data: status } = await axios.get(`${API}/training/status/${data.job_id}`, { withCredentials: true });
          setTrainingProgress(status);
          if (status.status === 'completed') {
            setIsTraining(false);
            toast.success('Entrenamiento completado!');
            fetchData();
          } else if (status.status === 'failed') {
            setIsTraining(false);
            toast.error('Entrenamiento fallo: ' + (status.error || ''));
          } else {
            setTimeout(pollProgress, 3000);
          }
        } catch (err) {
          setIsTraining(false);
        }
      };
      pollProgress();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar entrenamiento');
      setIsTraining(false);
    }
  };

  const downloadModel = async (modelId) => {
    try {
      const response = await axios.get(`${API}/training/models/${modelId}/download`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url; a.download = `modelo_patentes_${modelId}.pt`; a.click();
    } catch (err) {
      toast.error('Error al descargar modelo');
    }
  };

  const handleImportZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/training/import-zip`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      setShowImportModal(false);
      fetchData();
    } catch (err) {
      toast.error('Error al importar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportRoboflow = async () => {
    if (!roboflowApiKey || !roboflowWorkspace || !roboflowProject) {
      toast.error('Completa todos los campos de Roboflow');
      return;
    }
    setIsImporting(true);
    try {
      const { data } = await axios.post(`${API}/training/import-roboflow`, {
        api_key: roboflowApiKey, workspace: roboflowWorkspace,
        project: roboflowProject, version: parseInt(roboflowVersion) || 1
      }, { withCredentials: true });
      toast.success(data.message);
      setShowImportModal(false);
      setRoboflowApiKey(''); setRoboflowWorkspace(''); setRoboflowProject('');
      fetchData();
    } catch (err) {
      toast.error('Error al importar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsImporting(false);
    }
  };

  const annotatedCount = images.filter(i => i.annotations?.length > 0).length;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />Volver
                </Button>
              </Link>
              <div className="border-l border-slate-700 pl-4">
                <h1 className="text-lg font-bold text-white">Entrenamiento</h1>
                <p className="text-sm text-slate-400">Entrena modelos para detectar patentes</p>
              </div>
            </div>
            <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        {/* Training Status Banner */}
        {trainingProgress && (
          <Card className={`mb-6 ${
            trainingProgress.status === 'completed' ? 'bg-emerald-900/50 border-emerald-600' :
            trainingProgress.status === 'failed' ? 'bg-red-900/50 border-red-600' :
            'bg-blue-900/50 border-blue-600'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {trainingProgress.status === 'completed' ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  ) : trainingProgress.status === 'failed' ? (
                    <X className="w-5 h-5 text-red-400" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                  )}
                  <span className="text-white font-bold">
                    {trainingProgress.status === 'completed' ? 'Entrenamiento Completado!' :
                     trainingProgress.status === 'failed' ? 'Entrenamiento Fallo' :
                     trainingProgress.status === 'training' ? `Entrenando... Epoca ${trainingProgress.current_epoch || 0}/${trainingProgress.total_epochs || 30}` :
                     'Preparando entrenamiento...'}
                  </span>
                </div>
                <Badge className={
                  trainingProgress.status === 'completed' ? 'bg-emerald-600' :
                  trainingProgress.status === 'failed' ? 'bg-red-600' :
                  'bg-blue-600'
                }>
                  {trainingProgress.progress || 0}%
                </Badge>
              </div>
              <Progress value={trainingProgress.progress || 0} className="h-2" />
              {trainingProgress.status === 'failed' && trainingProgress.error && (
                <p className="text-red-300 text-sm mt-2">{trainingProgress.error}</p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left panel - Images list */}
          <div className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-base">Imagenes ({images.length})</CardTitle>
                  <div className="flex gap-1">
                    <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleUpload} className="hidden" />
                    <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-btn">
                      {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowImportModal(true)} className="border-slate-600 text-slate-300" data-testid="import-btn">
                      <Cloud className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-2">
                {images.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Sube imagenes para comenzar</p>
                  </div>
                ) : images.map((img) => (
                  <div
                    key={img.id}
                    onClick={() => loadImage(img)}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                      selectedImage?.id === img.id ? 'bg-blue-600/30 border border-blue-500' : 'bg-slate-700/50 hover:bg-slate-700'
                    }`}
                    data-testid={`image-item-${img.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ImageIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-white text-sm truncate">{img.filename}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {img.annotations?.length > 0 && (
                        <Badge variant="outline" className="text-emerald-400 border-emerald-400/50 text-xs">
                          {img.annotations.length}
                        </Badge>
                      )}
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteImage(img.id); }} className="h-6 w-6 p-0 text-red-400 hover:text-red-300">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Training section */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Entrenamiento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Imagenes anotadas:</span>
                  <span className={annotatedCount >= 5 ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>
                    {annotatedCount}/{images.length}
                  </span>
                </div>
                <Button
                  onClick={startTraining}
                  disabled={isTraining || annotatedCount < 5}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="start-training-btn"
                >
                  {isTraining ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Entrenando...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" />Iniciar Entrenamiento</>
                  )}
                </Button>
                {annotatedCount < 5 && (
                  <p className="text-amber-400 text-xs text-center">Necesitas al menos 5 imagenes anotadas</p>
                )}
              </CardContent>
            </Card>

            {/* Models */}
            {models.length > 0 && (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base">Modelos ({models.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {models.map((model) => (
                    <div key={model.id} className="flex items-center justify-between p-2 bg-slate-700/50 rounded" data-testid={`model-${model.id}`}>
                      <div>
                        <p className="text-white text-sm">{model.epochs} epocas</p>
                        <p className="text-slate-400 text-xs">{new Date(model.created_at).toLocaleDateString('es-AR')}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => downloadModel(model.id)} className="border-slate-600 text-slate-300">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Center + Right - Annotation area */}
          <div className="lg:col-span-2">
            {selectedImage ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-base truncate">{selectedImage.filename}</CardTitle>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={autoLabelImage} disabled={isAutoLabeling} className="bg-purple-600 hover:bg-purple-700" data-testid="auto-label-btn">
                        {isAutoLabeling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                        Auto-etiquetar IA
                      </Button>
                      <Button size="sm" onClick={saveAnnotations} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-annotations-btn">
                        <Save className="w-4 h-4 mr-1" />Guardar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="relative bg-slate-900 rounded overflow-hidden mb-4">
                    <img
                      ref={imageRef}
                      src={`${API}/training/images/${selectedImage.id}/full`}
                      alt={selectedImage.filename}
                      className="hidden"
                      crossOrigin="use-credentials"
                      onLoad={handleImageLoad}
                    />
                    <canvas
                      ref={canvasRef}
                      className="w-full cursor-crosshair"
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      data-testid="annotation-canvas"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-slate-400 text-sm">Anotaciones ({annotations.length})</p>
                      <p className="text-slate-500 text-xs">Dibuja rectangulos sobre las patentes</p>
                    </div>
                    {annotations.map((ann, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-slate-700/50 rounded" data-testid={`annotation-${i}`}>
                        <div className="flex items-center gap-2">
                          <Tag className="w-3 h-3 text-emerald-400" />
                          <span className="text-white text-sm">Patente #{i + 1}</span>
                          <span className="text-slate-400 text-xs">
                            ({Math.round(ann.x_center * 100)}%, {Math.round(ann.y_center * 100)}%)
                          </span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeAnnotation(i)} className="h-6 w-6 p-0 text-red-400">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-800 border-slate-700 h-full flex items-center justify-center">
                <CardContent className="text-center py-20">
                  <ImageIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 text-lg mb-2">Selecciona una imagen</p>
                  <p className="text-slate-500 text-sm">Sube imagenes y seleccionalas para anotar las patentes</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <footer className="py-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-3 flex flex-col sm:flex-row items-center justify-between gap-1">
          <img src={DISRUPTIVE_LOGO} alt="Disruptive Software" className="h-7 w-auto invert" />
          <span className="text-slate-500 text-xs">Desarrollado por <a href="https://www.disruptive-sw.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Disruptive Software</a>. Todos los derechos reservados.</span><span className="text-slate-500 text-xs ml-auto"><a href="mailto:info@disruptive-sw.com" className="hover:text-white">info@disruptive-sw.com</a></span>
        </div>
      </footer>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Importar Dataset</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="zip" className="mt-4">
            <TabsList className="bg-slate-700">
              <TabsTrigger value="zip"><FileArchive className="w-4 h-4 mr-1" />ZIP</TabsTrigger>
              <TabsTrigger value="roboflow"><Cloud className="w-4 h-4 mr-1" />Roboflow</TabsTrigger>
            </TabsList>
            <TabsContent value="zip" className="space-y-4">
              <p className="text-slate-400 text-sm">Sube un archivo ZIP con formato YOLO (carpetas images/ y labels/).</p>
              <input ref={zipInputRef} type="file" accept=".zip" onChange={handleImportZip} className="hidden" />
              <Button onClick={() => zipInputRef.current?.click()} disabled={isImporting} className="w-full bg-blue-600 hover:bg-blue-700">
                {isImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Seleccionar ZIP
              </Button>
            </TabsContent>
            <TabsContent value="roboflow" className="space-y-4">
              <div>
                <Label className="text-slate-300 text-sm">API Key</Label>
                <Input value={roboflowApiKey} onChange={(e) => setRoboflowApiKey(e.target.value)} className="bg-slate-700 border-slate-600 text-white mt-1" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Workspace</Label>
                <Input value={roboflowWorkspace} onChange={(e) => setRoboflowWorkspace(e.target.value)} className="bg-slate-700 border-slate-600 text-white mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-sm">Proyecto</Label>
                  <Input value={roboflowProject} onChange={(e) => setRoboflowProject(e.target.value)} className="bg-slate-700 border-slate-600 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Version</Label>
                  <Input value={roboflowVersion} onChange={(e) => setRoboflowVersion(e.target.value)} className="bg-slate-700 border-slate-600 text-white mt-1" />
                </div>
              </div>
              <Button onClick={handleImportRoboflow} disabled={isImporting} className="w-full bg-purple-600 hover:bg-purple-700">
                {isImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Importar de Roboflow
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
